import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Avatar } from '../../components/ui/Avatar';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { ApiError, api } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { subscribeToJam, type JamMessage } from '../../lib/realtime';
import { useSession } from '../../stores/session';

const LIMIT = 500;
/**
 * How close to the bottom still counts as being at it.
 *
 * Roughly one line. A reader who has nudged the list by half of one has
 * not gone looking for anything, and treating that as "reading further up"
 * is how a panel stops following the conversation.
 */
const NEAR = 80;

/**
 * The jam's chat: a room's talk, for as long as the room exists.
 *
 * ## Beside the queue, not under it
 *
 * What people say in a jam is about what is playing, so it sits next to
 * the queue on a wide screen and under it on a narrow one - where a column
 * of chat beside a column of tracks would be two unreadable columns.
 *
 * ## It follows the socket, and asks once
 *
 * One fetch of the last hundred lines when the panel opens, then the jam's
 * channel. A line said anywhere arrives as `jam.said` with the line on it,
 * so nobody waits a round trip to see what they typed. A reconnect asks
 * for everything after the last id it holds rather than the window again.
 *
 * ## Nothing is kept
 *
 * The rows die with the jam (see the backend's `JamChat`), which is what
 * lets this be as loose as it is: no editing, no deleting, no inbox.
 */
export function JamChat({ code, jamId, canSay }: { code: string; jamId: number; canSay: boolean }) {
  const client = useQueryClient();
  const me = useSession((state) => state.user);
  const key = ['jam', code, 'chat'];

  const { data, isPending } = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ messages: JamMessage[] }>(`/jams/${code}/chat`),
    // The socket keeps it fresh; a refetch on every focus would be noise.
    staleTime: Infinity,
  });

  const messages = data?.messages ?? [];
  const lastId = messages.at(-1)?.id ?? null;

  // A line, from anywhere, without asking.
  useEffect(() => {
    return subscribeToJam(
      jamId,
      () => {},
      undefined,
      ({ message }) => {
        client.setQueryData<{ messages: JamMessage[] }>(key, (held) => {
          const held_ = held?.messages ?? [];

          if (held_.some((line) => line.id === message.id)) return held ?? { messages: [message] };

          return { messages: inOrder([...held_, message]) };
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jamId, code]);

  /*
   * A socket that dropped and came back has a gap in it. Asking for
   * everything after the last id held is cheaper than the window and, more
   * to the point, keeps the order somebody was reading.
   */
  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState !== 'visible' || lastId === null) return;

      void api
        .get<{ messages: JamMessage[] }>(`/jams/${code}/chat?after=${lastId}`)
        .then(({ messages: fresh }) => {
          if (fresh.length === 0) return;

          client.setQueryData<{ messages: JamMessage[] }>(key, (held) => ({
            messages: inOrder([...(held?.messages ?? []), ...fresh]),
          }));
        })
        .catch(() => {});
    };

    document.addEventListener('visibilitychange', onBack);

    return () => document.removeEventListener('visibilitychange', onBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, lastId]);

  const [text, setText] = useState('');
  /*
   * The site's picker and the site's insert-at-the-caret, the same pair
   * the messages composer and the comment box use. A jam is where people
   * answer a track with one character, so it belongs here more than most.
   */
  const emoji = useEmojiComposer(text, setText);
  /** The line in flight, so the next one waits for it. See `say`. */
  const sending = useRef<Promise<unknown>>(Promise.resolve());
  const [problem, setProblem] = useState<string | null>(null);

  const say = useMutation({
    /*
     * One at a time, in the order they were typed.
     *
     * Each line is its own request, and two sent a moment apart race: the
     * server stamps them in the order they arrive, so somebody's own two
     * lines could answer each other backwards. Chaining them costs a few
     * milliseconds on the second and makes the order the one they meant.
     */
    mutationFn: (body: string) => {
      const sent = sending.current
        .catch(() => {})
        .then(() => api.post<{ message: JamMessage }>(`/jams/${code}/chat`, { body }));

      sending.current = sent.then(
        () => {},
        () => {},
      );

      return sent;
    },
    onSuccess: ({ message }) => {
      client.setQueryData<{ messages: JamMessage[] }>(key, (held) => {
        const held_ = held?.messages ?? [];

        // The socket may have delivered this already; whoever is second is a no-op.
        if (held_.some((line) => line.id === message.id)) return held ?? { messages: [message] };

        return { messages: [...held_, message].slice(-100) };
      });
      setProblem(null);
    },
    /*
     * The line goes back in the box on a failure, because somebody typed
     * it and it has not been said. Only when the box is still empty: they
     * may be part way through the next one by now.
     */
    onError: (error, body) => {
      setProblem(error instanceof ApiError ? error.message : 'That did not send.');
      setText((held) => (held === '' ? body : held));
    },
  });

  /*
   * Pinned to the newest line, unless somebody has scrolled up to read.
   *
   * ## Watched, not timed
   *
   * This used to scroll one animation frame after the number of messages
   * changed, and that is right only if the panel's height is final in that
   * frame. Often it is not: a line that wraps to three, an avatar that
   * arrives late, a window that changes size, a frame that never runs at
   * all because the tab is in the background and the next render cancels
   * it. A `ResizeObserver` asks the browser to say when the content has
   * actually grown, which is the moment this always wanted.
   *
   * ## And decided from the geometry, not remembered
   *
   * Whether the reader is at the bottom used to be a flag set by scroll
   * events. Anything that set it wrongly once - a scroll event dispatched
   * mid-layout, a line tall enough to push the distance past the
   * threshold - left it false for the rest of the jam, because nothing but
   * a scroll back down ever set it true again. That is a panel that
   * follows the conversation once after a refresh and then stops, which is
   * exactly what it did.
   *
   * So there is no flag. Each time the box or its contents change size,
   * the question is asked from scratch, against the size they were *before*
   * the change: content that grew below the reader has not moved them, and
   * a panel that got shorter under them has not either. Both are taken off
   * the distance before it is measured. Wrong once means wrong once.
   *
   * Somebody who has scrolled up is left where they are: a chat that yanks
   * you back down mid-sentence is why people stop reading them.
   */
  const list = useRef<HTMLDivElement | null>(null);
  const lines = useRef<HTMLDivElement | null>(null);
  /** Whether the newest line is out of sight, for the button that goes to it. */
  const [adrift, setAdrift] = useState(false);

  useEffect(() => {
    const box = list.current;
    const content = lines.current;

    if (!box || !content) return;

    let wasContent = box.scrollHeight;
    let wasBox = box.clientHeight;

    const settle = () => {
      const grew = Math.max(0, box.scrollHeight - wasContent);
      const shrank = Math.max(0, wasBox - box.clientHeight);

      wasContent = box.scrollHeight;
      wasBox = box.clientHeight;

      // How far from the bottom the reader was, before this change moved it.
      const distance = box.scrollHeight - box.scrollTop - box.clientHeight - grew - shrank;

      if (distance < NEAR) {
        box.scrollTop = box.scrollHeight;
        setAdrift(false);

        return;
      }

      // Left where they are, and told there is something new below them -
      // but only when something new actually arrived.
      if (grew > 0) setAdrift(true);
    };

    // Once now, for a chat opened mid-conversation, and then whenever the
    // lines take up more room than they did.
    settle();

    const observer = new ResizeObserver(settle);

    // The lines, for a message arriving or re-wrapping; the box, for the
    // panel itself getting shorter - a window resized, or a phone's
    // keyboard coming up under it, which is exactly when somebody is about
    // to type and least wants to be looking at the middle of the chat.
    observer.observe(content);
    observer.observe(box);

    return () => observer.disconnect();
  }, []);

  /** Back to the newest line, from the button or after saying something. */
  const toNewest = () => {
    const box = list.current;

    if (!box) return;

    box.scrollTop = box.scrollHeight;
    setAdrift(false);
  };

  function submit(event: FormEvent) {
    event.preventDefault();

    const body = text.trim();

    if (body === '') return;

    /*
     * Not gated on the last one having landed. Two lines typed quickly are
     * two lines somebody said, and refusing the second because the first
     * is still in the air drops it with nothing to show for it - which is
     * exactly what a chat must never do. The box empties now and each
     * request stands on its own; the server orders them by arrival.
     */
    setText('');
    say.mutate(body.slice(0, LIMIT));

    /*
     * Saying something is asking to see it. Whatever the panel had decided
     * about where this reader was looking, they are looking at their own
     * line now - and the alternative is typing into a conversation that
     * scrolls somewhere else.
     */
    toNewest();
  }

  return (
    <div className="flex min-h-0 w-full flex-col overflow-hidden bg-surface" data-jam-chat>
      <h2 className="meta-caps border-b border-line px-4 py-3 text-faint">
        Chat
        <span className="ml-2 text-ghost">it ends with the jam</span>
      </h2>

      <div
        ref={list}
        onScroll={(event) => {
          const box = event.currentTarget;

          // Only to take the button away again when they come back down.
          if (box.scrollHeight - box.scrollTop - box.clientHeight < NEAR) setAdrift(false);
        }}
        className="relative min-h-0 flex-1 overflow-y-auto px-4 py-3"
        data-jam-chat-lines
      >
        {/*
          One element around the lines, so their height can be watched. The
          scroll box's own height is whatever the layout gives it and never
          changes when a line arrives. See the effect above.
        */}
        <div ref={lines} className="flex flex-col gap-3">
        {isPending && <p className="text-[13px] text-faint">Loading…</p>}

        {!isPending && messages.length === 0 && (
          <p className="text-[13px] text-faint">
            Nothing said yet. Whatever goes here goes to everybody in the jam, and disappears when
            the jam ends.
          </p>
        )}

        {messages.map((line) => (
          <div key={line.id} className="flex gap-2.5" data-jam-chat-line={line.id}>
            <Avatar
              src={line.by?.avatar ?? null}
              name={line.by?.display_name ?? 'Someone'}
              size={28}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="truncate text-[13px] font-medium text-ink">
                  {line.by?.id === me?.id ? 'You' : (line.by?.display_name ?? 'Someone')}
                </span>
                <span className="meta shrink-0 text-ghost">{clock(line.at)}</span>
              </div>
              <p className="text-[13px] break-words text-soft">{line.body}</p>
            </div>
          </div>
        ))}
        </div>
      </div>

      {/*
        The way back, when somebody has scrolled up and the room has said
        something since. Outside the scrolling box so it stays put, and
        only while there is something below to go to.
      */}
      {adrift && (
        <div className="relative">
          <button
            type="button"
            onClick={toNewest}
            data-jam-chat-to-newest
            className="absolute -top-10 left-1/2 -translate-x-1/2 rounded-[var(--radius-pill)] border border-line-lit bg-raised px-3 py-1.5 text-[12px] text-ink shadow-lg transition-colors hover:bg-slate"
          >
            New messages ↓
          </button>
        </div>
      )}

      {canSay && (
        <form onSubmit={submit} className="flex items-center gap-2 border-t border-line p-3">
          <EmojiButton onSelect={emoji.insert} />

          <input
            ref={emoji.ref as React.RefObject<HTMLInputElement>}
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={LIMIT}
            placeholder="Say something"
            aria-label="Say something in the jam"
            data-jam-chat-input
            className="min-w-0 flex-1 rounded-[var(--radius-pill)] border border-line bg-raised px-3.5 py-2 text-sm text-ink placeholder:text-ghost focus:border-line-lit focus:outline-none"
          />
          <button
            type="submit"
            disabled={text.trim() === ''}
            data-jam-chat-send
            className="shrink-0 rounded-[var(--radius-pill)] bg-accent px-3.5 py-2 text-[13px] font-medium text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-40"
          >
            Send
          </button>
        </form>
      )}

      {problem !== null && <p className="px-4 pb-3 text-[13px] text-negative">{problem}</p>}
    </div>
  );
}

/**
 * By id, which is the order the server accepted them in.
 *
 * Lines arrive from three places - the socket, this person's own send,
 * and a catch-up fetch - and two said a moment apart can reach a page in
 * the other order. The id is the only thing that knows which came first,
 * and a chat whose lines answer each other backwards is unreadable.
 */
function inOrder(lines: JamMessage[]): JamMessage[] {
  return [...lines].sort((a, b) => a.id - b.id).slice(-100);
}

/** `14:32`, because a jam is an hour long at most and a date says nothing. */
function clock(iso: string): string {
  const at = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));

  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
